import React, { useState, useEffect, useMemo } from 'react';
import api from '../services/api';
import './Employment.css';

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const CURRENCIES = ['GBP', 'EUR', 'USD', 'CHF', 'CAD', 'AUD'];
const SALARY_PERIODS = [
  { value: 'hourly', label: 'Per Hour' },
  { value: 'daily', label: 'Per Day' },
  { value: 'weekly', label: 'Per Week' },
  { value: 'monthly', label: 'Per Month' },
  { value: 'yearly', label: 'Per Year' }
];

// Helper to get sort date (ongoing jobs use far future date)
const getSortDate = (job) => {
  if (job.is_ongoing || !job.employment_end_date) {
    return new Date('2099-12-31');
  }
  return new Date(job.employment_end_date);
};

// Helper to calculate total duration at company
const calculateCompanyDuration = (jobs) => {
  let totalMonths = 0;
  jobs.forEach(job => {
    const start = job.employment_start_date ? new Date(job.employment_start_date) : new Date();
    const end = job.is_ongoing || !job.employment_end_date ? new Date() : new Date(job.employment_end_date);
    const months = (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth());
    totalMonths += Math.max(0, months);
  });
  
  const years = Math.floor(totalMonths / 12);
  const months = totalMonths % 12;
  
  if (years > 0 && months > 0) return `${years} yr ${months} mo`;
  if (years > 0) return `${years} yr${years > 1 ? 's' : ''}`;
  if (months > 0) return `${months} mo${months > 1 ? 's' : ''}`;
  return 'Less than a month';
};

function Employment() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedJobId, setExpandedJobId] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);

  useEffect(() => {
    fetchJobs();
  }, []);

  const fetchJobs = async () => {
    try {
      setLoading(true);
      const response = await api.get('/employment/jobs');
      setJobs(response.data);
    } catch (err) {
      console.error('Error fetching jobs:', err);
      setError('Failed to load jobs');
    } finally {
      setLoading(false);
    }
  };

  // Group jobs by company and sort
  const groupedJobs = useMemo(() => {
    // Sort all jobs by end date (most recent first, ongoing at top)
    const sortedJobs = [...jobs].sort((a, b) => getSortDate(b) - getSortDate(a));
    
    // Group by company name (normalize empty/null to "Independent")
    const groups = {};
    sortedJobs.forEach(job => {
      const company = job.company_name?.trim() || 'Independent / Freelance';
      if (!groups[company]) {
        groups[company] = {
          company,
          jobs: [],
          hasOngoing: false,
          latestDate: null
        };
      }
      groups[company].jobs.push(job);
      if (job.is_ongoing || !job.employment_end_date) {
        groups[company].hasOngoing = true;
      }
      const jobDate = getSortDate(job);
      if (!groups[company].latestDate || jobDate > groups[company].latestDate) {
        groups[company].latestDate = jobDate;
      }
    });
    
    // Convert to array and sort groups (ongoing companies first, then by latest job date)
    return Object.values(groups).sort((a, b) => {
      if (a.hasOngoing && !b.hasOngoing) return -1;
      if (!a.hasOngoing && b.hasOngoing) return 1;
      return b.latestDate - a.latestDate;
    });
  }, [jobs]);

  const handleDeleteJob = async (jobId) => {
    if (!window.confirm('Are you sure you want to delete this job? This cannot be undone.')) {
      return;
    }
    try {
      await api.delete(`/employment/jobs/${jobId}`);
      setJobs(jobs.filter(j => j.id !== jobId));
      if (expandedJobId === jobId) setExpandedJobId(null);
    } catch (err) {
      alert('Failed to delete job');
    }
  };

  const handleSetCurrent = async (jobId) => {
    try {
      await api.post(`/employment/jobs/${jobId}/set-current`);
      setJobs(jobs.map(j => ({
        ...j,
        is_current: j.id === jobId
      })));
    } catch (err) {
      alert('Failed to set job as current');
    }
  };

  const toggleExpanded = (jobId) => {
    setExpandedJobId(expandedJobId === jobId ? null : jobId);
  };

  const formatDate = (dateStr, isOngoing) => {
    if (isOngoing) return 'Present';
    if (!dateStr) return 'Present';
    return new Date(dateStr).toLocaleDateString('en-GB', { 
      month: 'short', 
      year: 'numeric' 
    });
  };

  const formatSalary = (amount, currency, period) => {
    if (!amount) return null;
    const formatted = new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: currency || 'GBP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
    const periodLabel = SALARY_PERIODS.find(p => p.value === period)?.label || 'Per Year';
    return `${formatted} ${periodLabel}`;
  };

  if (loading) {
    return (
      <div className="employment-page">
        <div className="loading-container">
          <div className="loading-spinner" />
          <p>Loading employment data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="employment-page">
      <header className="employment-header">
        <div className="header-left">
          <h1>💼 Employment</h1>
          <p className="header-subtitle">Manage your jobs, salary, and leave allowances</p>
        </div>
        <div className="header-right">
          <button className="btn-primary" onClick={() => setShowAddModal(true)}>
            + Add Job
          </button>
        </div>
      </header>

      {error && (
        <div className="error-banner">
          {error}
          <button onClick={fetchJobs}>Retry</button>
        </div>
      )}

      <div className="jobs-container">
        {jobs.length === 0 ? (
          <div className="no-jobs">
            <span className="empty-icon">💼</span>
            <h3>No jobs added yet</h3>
            <p>Add your employment details to track leave allowances and salary</p>
            <button className="btn-primary" onClick={() => setShowAddModal(true)}>
              Add Your First Job
            </button>
          </div>
        ) : (
          <div className="jobs-timeline">
            {groupedJobs.map(group => {
              const isMultipleRoles = group.jobs.length > 1;
              
              return (
                <div key={group.company} className={`company-group ${isMultipleRoles ? 'multiple' : 'single'}`}>
                  {/* Company Header (only for multiple roles) */}
                  {isMultipleRoles && (
                    <div className="company-header">
                      <div className="company-icon">🏢</div>
                      <div className="company-info">
                        <h3 className="company-title">{group.company}</h3>
                        <span className="company-duration">
                          {group.jobs.length} roles · {calculateCompanyDuration(group.jobs)}
                        </span>
                      </div>
                      {group.hasOngoing && <span className="active-badge">Active</span>}
                    </div>
                  )}
                  
                  {/* Jobs list */}
                  <div className={`jobs-list ${isMultipleRoles ? 'nested' : ''}`}>
                    {group.jobs.map((job, index) => {
                      const hasEnded = job.employment_end_date && new Date(job.employment_end_date) < new Date();
                      const isActive = job.is_ongoing || !hasEnded;
                      const showCurrentBadge = job.is_current && isActive;
                      const isLast = index === group.jobs.length - 1;
                      
                      return (
                        <div 
                          key={job.id} 
                          className={`job-card ${expandedJobId === job.id ? 'expanded' : ''} ${job.is_current ? 'current' : ''} ${hasEnded ? 'ended' : ''} ${isMultipleRoles ? 'nested-job' : ''} ${isLast ? 'last' : ''}`}
                        >
                          {/* Timeline connector for nested jobs */}
                          {isMultipleRoles && (
                            <div className="timeline-connector">
                              <div className="timeline-dot" />
                              {!isLast && <div className="timeline-line" />}
                            </div>
                          )}
                          
                          <div className="job-card-content">
                            <div className="job-card-header" onClick={() => toggleExpanded(job.id)}>
                              <div className="job-info">
                                <div className="job-title-row">
                                  <h3 className="job-name">{job.job_name}</h3>
                                  {showCurrentBadge && <span className="current-badge">Current</span>}
                                  {job.is_ongoing && <span className="ongoing-badge">Ongoing</span>}
                                  {hasEnded && <span className="ended-badge">Ended</span>}
                                  {job.is_current && hasEnded && (
                                    <span className="warning-badge" title="This job has ended but is still marked as current">⚠️</span>
                                  )}
                                </div>
                                {/* Show company name only for single-job groups */}
                                {!isMultipleRoles && job.company_name && (
                                  <div className="company-name">🏢 {job.company_name}</div>
                                )}
                                <div className="job-dates">
                                  <span className="date-range">
                                    {formatDate(job.employment_start_date)} → {formatDate(job.employment_end_date, job.is_ongoing)}
                                  </span>
                                </div>
                                <div className="job-summary">
                                  <span className="summary-item">
                                    🏖️ {job.base_days + job.carried_over + job.extra_days} days/year
                                  </span>
                                  {job.salary_amount && (
                                    <span className="summary-item salary">
                                      💰 {formatSalary(job.salary_amount, job.salary_currency, job.salary_period)}
                                    </span>
                                  )}
                                  <span className="summary-item">
                                    ⏰ {job.hours_per_day}h/day
                                  </span>
                                </div>
                              </div>
                              <div className="job-actions">
                                <span className="expand-icon">{expandedJobId === job.id ? '▲' : '▼'}</span>
                              </div>
                            </div>

                            {expandedJobId === job.id && (
                              <JobDetails 
                                job={job}
                                onUpdate={(updatedJob) => {
                                  setJobs(jobs.map(j => j.id === updatedJob.id ? updatedJob : j));
                                }}
                                onDelete={() => handleDeleteJob(job.id)}
                                onSetCurrent={() => handleSetCurrent(job.id)}
                              />
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {showAddModal && (
        <AddJobModal
          onClose={() => setShowAddModal(false)}
          onSuccess={(newJob) => {
            setJobs([newJob, ...jobs]);
            setShowAddModal(false);
            setExpandedJobId(newJob.id);
          }}
        />
      )}
    </div>
  );
}

// Job Details Component (shown when expanded)
function JobDetails({ job, onUpdate, onDelete, onSetCurrent }) {
  const [settings, setSettings] = useState({ ...job });
  const [saving, setSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null);

  const updateSettings = (newSettings) => {
    setSettings(newSettings);
    setIsDirty(true);
    setSaveStatus(null);
  };

  const toggleWorkingDay = (dayIndex) => {
    const currentDays = settings.working_days || [1, 2, 3, 4, 5];
    let newDays;
    if (currentDays.includes(dayIndex)) {
      newDays = currentDays.filter(d => d !== dayIndex);
    } else {
      newDays = [...currentDays, dayIndex].sort((a, b) => a - b);
    }
    updateSettings({ ...settings, working_days: newDays });
  };

  const saveSettings = async () => {
    setSaving(true);
    setSaveStatus(null);
    try {
      await api.put(`/employment/jobs/${job.id}`, settings);
      setSaveStatus('success');
      setIsDirty(false);
      onUpdate({ ...job, ...settings });
      setTimeout(() => setSaveStatus(null), 3000);
    } catch (err) {
      console.error('Error saving job:', err);
      setSaveStatus('error');
    } finally {
      setSaving(false);
    }
  };

  // Calculate pro-rata considering both start AND end dates
  // Uses continuous_from_date if set (for role changes within same company)
  const calculateProrata = () => {
    const total = (settings.base_days || 0) + (settings.carried_over || 0) + (settings.extra_days || 0);
    // Use continuous_from_date if set, otherwise use employment_start_date
    const effectiveStartDate = settings.continuous_from_date || settings.employment_start_date;
    const jobStart = effectiveStartDate ? new Date(effectiveStartDate) : null;
    const jobEnd = settings.is_ongoing ? null : (settings.employment_end_date ? new Date(settings.employment_end_date) : null);
    const today = new Date();
    
    const getFiscalYear = (date) => {
      const y = date.getFullYear();
      const m = date.getMonth();
      const d = date.getDate();
      
      if (settings.year_type === 'fiscal') {
        if (m < 3 || (m === 3 && d < 6)) {
          return { start: new Date(y - 1, 3, 6), end: new Date(y, 3, 5), label: `FY ${y - 1}/${String(y).slice(-2)}` };
        } else {
          return { start: new Date(y, 3, 6), end: new Date(y + 1, 3, 5), label: `FY ${y}/${String(y + 1).slice(-2)}` };
        }
      } else if (settings.year_type === 'custom' && settings.fiscal_year_start) {
        const fs = new Date(settings.fiscal_year_start);
        const fsMonth = fs.getMonth();
        const fsDay = fs.getDate();
        if (m < fsMonth || (m === fsMonth && d < fsDay)) {
          return { start: new Date(y - 1, fsMonth, fsDay), end: new Date(y, fsMonth, fsDay - 1), label: `${y - 1}/${String(y).slice(-2)}` };
        } else {
          return { start: new Date(y, fsMonth, fsDay), end: new Date(y + 1, fsMonth, fsDay - 1), label: `${y}/${String(y + 1).slice(-2)}` };
        }
      } else {
        return { start: new Date(y, 0, 1), end: new Date(y, 11, 31), label: String(y) };
      }
    };
    
    // Use current fiscal year for calculation
    const currentFY = getFiscalYear(today);
    
    // Determine effective period within the fiscal year
    let effectiveStart = currentFY.start;
    let effectiveEnd = currentFY.end;
    let reason = null;
    let startedInYear = false;
    let endingInYear = false;
    
    // If employment started within current fiscal year
    if (jobStart && jobStart > currentFY.start && jobStart <= currentFY.end) {
      effectiveStart = jobStart;
      startedInYear = true;
    }
    
    // If employment ends within current fiscal year
    if (jobEnd && jobEnd >= currentFY.start && jobEnd <= currentFY.end) {
      effectiveEnd = jobEnd;
      endingInYear = true;
    }
    
    // Determine reason
    if (startedInYear && endingInYear) {
      reason = 'started_and_ending';
    } else if (startedInYear) {
      reason = 'started_mid_year';
    } else if (endingInYear) {
      reason = 'ending_mid_year';
    }
    
    // If neither affects the year, no pro-rata needed
    if (!startedInYear && !endingInYear) {
      return { 
        currentFY, 
        percentage: 100, 
        prorated: total.toFixed(1), 
        total, 
        totalDaysInYear: Math.round((currentFY.end - currentFY.start) / (1000 * 60 * 60 * 24)) + 1,
        workedDays: Math.round((currentFY.end - currentFY.start) / (1000 * 60 * 60 * 24)) + 1,
        applies: false,
        reason: null,
        effectiveStart,
        effectiveEnd
      };
    }
    
    const totalDaysInYear = Math.round((currentFY.end - currentFY.start) / (1000 * 60 * 60 * 24)) + 1;
    const workedDays = Math.max(0, Math.round((effectiveEnd - effectiveStart) / (1000 * 60 * 60 * 24)) + 1);
    const fraction = workedDays / totalDaysInYear;
    const prorated = (total * fraction).toFixed(1);
    const percentage = Math.round(fraction * 100);
    
    return { 
      currentFY, 
      percentage, 
      prorated, 
      total, 
      totalDaysInYear, 
      workedDays,
      applies: true,
      reason,
      effectiveStart,
      effectiveEnd,
      startedInYear,
      endingInYear
    };
  };

  const prorata = calculateProrata();

  return (
    <div className="job-details">
      {/* Save Header */}
      <div className={`save-header ${saveStatus || (isDirty ? 'dirty' : 'saved')}`}>
        <span className="save-status">
          {saveStatus === 'success' ? '✅ Saved!' : 
           saveStatus === 'error' ? '❌ Error saving' :
           isDirty ? '⚠️ Unsaved changes' : '✅ All saved'}
        </span>
        <div className="save-actions">
          {!job.is_current && (
            <button className="btn-outline" onClick={onSetCurrent}>
              ⭐ Set as Current
            </button>
          )}
          <button 
            className="btn-primary" 
            onClick={saveSettings} 
            disabled={saving || !isDirty}
          >
            {saving ? 'Saving...' : '💾 Save'}
          </button>
        </div>
      </div>

      {/* Job Info Section */}
      <div className="detail-section">
        <h4>📝 Job Information</h4>
        <div className="form-row">
          <div className="form-field">
            <label>Job Title / Role</label>
            <input
              type="text"
              value={settings.job_name || ''}
              onChange={(e) => updateSettings({ ...settings, job_name: e.target.value })}
              placeholder="e.g. Software Engineer"
            />
          </div>
          <div className="form-field">
            <label>Company Name</label>
            <input
              type="text"
              value={settings.company_name || ''}
              onChange={(e) => updateSettings({ ...settings, company_name: e.target.value })}
              placeholder="e.g. Acme Corp"
            />
          </div>
        </div>
      </div>

      {/* Job Period */}
      <div className="detail-section">
        <h4>📅 Employment Period</h4>
        <div className="date-row">
          <div className="date-field">
            <label>Start Date</label>
            <input
              type="date"
              value={settings.employment_start_date || ''}
              onChange={(e) => updateSettings({ ...settings, employment_start_date: e.target.value || null })}
            />
          </div>
          <div className="date-field">
            <label>End Date</label>
            <input
              type="date"
              value={settings.employment_end_date || ''}
              onChange={(e) => updateSettings({ ...settings, employment_end_date: e.target.value || null })}
              disabled={settings.is_ongoing}
            />
          </div>
        </div>
        <label className="toggle-option ongoing-toggle">
          <input
            type="checkbox"
            checked={settings.is_ongoing || false}
            onChange={(e) => updateSettings({ 
              ...settings, 
              is_ongoing: e.target.checked,
              employment_end_date: e.target.checked ? null : settings.employment_end_date
            })}
          />
          <span>This is an ongoing position (still employed)</span>
        </label>
      </div>

      {/* Continuous Employment */}
      <div className="detail-section continuous-employment">
        <h4>🔗 Continuous Employment</h4>
        <p className="section-hint">
          If you changed roles but stayed at the same company, set the date when your continuous employment started.
          This ensures your leave entitlement isn't pro-rated for role changes.
        </p>
        <div className="date-row">
          <div className="date-field">
            <label>Continuous from date</label>
            <input
              type="date"
              value={settings.continuous_from_date || ''}
              onChange={(e) => updateSettings({ ...settings, continuous_from_date: e.target.value || null })}
              placeholder="Leave empty if this is your first role"
            />
            <span className="field-hint">Leave empty if this is your first role at this company</span>
          </div>
        </div>
        {settings.continuous_from_date && settings.employment_start_date && (
          <div className="continuous-info">
            <span className="info-badge">ℹ️</span>
            <span>
              Leave will be calculated as if you started on {new Date(settings.continuous_from_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
              {' '}instead of {new Date(settings.employment_start_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
            </span>
          </div>
        )}
      </div>

      {/* Salary Section */}
      <div className="detail-section">
        <h4>💰 Salary</h4>
        <div className="salary-row">
          <div className="form-field salary-amount">
            <label>Amount</label>
            <input
              type="number"
              value={settings.salary_amount || ''}
              onChange={(e) => updateSettings({ ...settings, salary_amount: parseFloat(e.target.value) || null })}
              placeholder="e.g. 50000"
              min="0"
              step="100"
            />
          </div>
          <div className="form-field salary-currency">
            <label>Currency</label>
            <select
              value={settings.salary_currency || 'GBP'}
              onChange={(e) => updateSettings({ ...settings, salary_currency: e.target.value })}
            >
              {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div className="form-field salary-period">
            <label>Period</label>
            <select
              value={settings.salary_period || 'yearly'}
              onChange={(e) => updateSettings({ ...settings, salary_period: e.target.value })}
            >
              {SALARY_PERIODS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
          </div>
        </div>
      </div>

      {/* Working Schedule Section */}
      <div className="detail-section">
        <h4>⏰ Working Schedule</h4>
        
        <div className="schedule-type-selector">
          <label className={`schedule-type-option ${settings.schedule_type === 'fixed' ? 'active' : ''}`}>
            <input
              type="radio"
              name={`schedule_${job.id}`}
              value="fixed"
              checked={settings.schedule_type === 'fixed'}
              onChange={() => updateSettings({ ...settings, schedule_type: 'fixed' })}
            />
            <div className="option-content">
              <span className="option-icon">🕐</span>
              <span className="option-title">Fixed Schedule</span>
              <span className="option-desc">Same hours every day</span>
            </div>
          </label>
          <label className={`schedule-type-option ${settings.schedule_type === 'variable' ? 'active' : ''}`}>
            <input
              type="radio"
              name={`schedule_${job.id}`}
              value="variable"
              checked={settings.schedule_type === 'variable'}
              onChange={() => updateSettings({ ...settings, schedule_type: 'variable' })}
            />
            <div className="option-content">
              <span className="option-icon">🔄</span>
              <span className="option-title">Variable Schedule</span>
              <span className="option-desc">Hours change weekly/daily</span>
            </div>
          </label>
        </div>

        <div className="hours-row">
          <div className="form-field">
            <label>Hours per Day {settings.schedule_type === 'variable' && '(Average)'}</label>
            <input
              type="number"
              value={settings.hours_per_day || 8}
              onChange={(e) => updateSettings({ ...settings, hours_per_day: parseFloat(e.target.value) || 8 })}
              min="0"
              max="24"
              step="0.5"
            />
          </div>
          <div className="form-field weekly-hours">
            <label>Weekly Hours</label>
            <input
              type="number"
              value={((settings.hours_per_day || 8) * (settings.working_days?.length || 5)).toFixed(1)}
              disabled
            />
          </div>
        </div>

        {settings.schedule_type === 'variable' && (
          <div className="form-field">
            <label>Schedule Notes</label>
            <textarea
              value={settings.schedule_notes || ''}
              onChange={(e) => updateSettings({ ...settings, schedule_notes: e.target.value })}
              placeholder="e.g. Week 1: Mon-Thu 9-5, Week 2: Tue-Fri 10-6..."
              rows={3}
            />
          </div>
        )}
      </div>

      {/* Working Days */}
      <div className="detail-section">
        <h4>📅 Working Days</h4>
        <div className="working-days-row">
          {WEEKDAYS.map((day, index) => (
            <label 
              key={index} 
              className={`day-checkbox ${settings.working_days?.includes(index) ? 'active' : ''}`}
            >
              <input
                type="checkbox"
                checked={settings.working_days?.includes(index) || false}
                onChange={() => toggleWorkingDay(index)}
              />
              <span>{day}</span>
            </label>
          ))}
        </div>
        <label className="toggle-option">
          <input
            type="checkbox"
            checked={settings.exclude_bank_holidays}
            onChange={(e) => updateSettings({ ...settings, exclude_bank_holidays: e.target.checked })}
          />
          <span>Exclude bank holidays from leave calculation</span>
        </label>
      </div>

      {/* Leave Year Type */}
      <div className="detail-section">
        <h4>📆 Leave Year Type</h4>
        <div className="year-type-options">
          {['calendar', 'fiscal', 'custom'].map(type => (
            <label 
              key={type} 
              className={`year-type-option ${settings.year_type === type ? 'active' : ''}`}
            >
              <input
                type="radio"
                name={`year_type_${job.id}`}
                value={type}
                checked={settings.year_type === type}
                onChange={() => updateSettings({ ...settings, year_type: type })}
              />
              <span className="option-title">
                {type === 'calendar' ? 'Calendar (Jan-Dec)' : 
                 type === 'fiscal' ? 'UK Fiscal (Apr-Apr)' : 'Custom'}
              </span>
            </label>
          ))}
        </div>
        {settings.year_type === 'custom' && (
          <div className="custom-start">
            <label>Start Month:</label>
            <select
              value={settings.fiscal_year_start ? new Date(settings.fiscal_year_start).getMonth() : 0}
              onChange={(e) => {
                const month = parseInt(e.target.value);
                const y = new Date().getFullYear();
                updateSettings({ ...settings, fiscal_year_start: `${y}-${String(month + 1).padStart(2, '0')}-01` });
              }}
            >
              {['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].map((m, i) => (
                <option key={i} value={i}>{m}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Leave Allowance */}
      <div className="detail-section">
        <h4>🏖️ Leave Allowance</h4>
        <div className="allowance-grid">
          <div className="allowance-item">
            <label>Base Days</label>
            <input
              type="number"
              value={settings.base_days || 0}
              onChange={(e) => updateSettings({ ...settings, base_days: parseInt(e.target.value) || 0 })}
              min="0"
            />
          </div>
          <div className="allowance-item">
            <label>Carried Over</label>
            <input
              type="number"
              value={settings.carried_over || 0}
              onChange={(e) => updateSettings({ ...settings, carried_over: parseInt(e.target.value) || 0 })}
              min="0"
            />
          </div>
          <div className="allowance-item">
            <label>Extra Days</label>
            <input
              type="number"
              value={settings.extra_days || 0}
              onChange={(e) => updateSettings({ ...settings, extra_days: parseInt(e.target.value) || 0 })}
              min="0"
            />
          </div>
          <div className="allowance-item">
            <label>Already Taken</label>
            <input
              type="number"
              value={settings.days_already_taken || 0}
              onChange={(e) => updateSettings({ ...settings, days_already_taken: parseInt(e.target.value) || 0 })}
              min="0"
            />
            <span className="field-hint">Before using app</span>
          </div>
          <div className="allowance-item total">
            <label>Total</label>
            <input
              type="number"
              value={(settings.base_days || 0) + (settings.carried_over || 0) + (settings.extra_days || 0)}
              disabled
            />
          </div>
        </div>
      </div>

      {/* Pro-rata Preview */}
      {prorata && (
        <div className={`detail-section prorata ${prorata.applies ? 'applies' : ''}`}>
          <h4>📊 Leave for {prorata.currentFY.label}</h4>
          
          {prorata.applies ? (
            <>
              <div className="prorata-reason">
                {prorata.reason === 'ending_mid_year' && (
                  <span className="reason-badge ending">⚠️ Employment ending {settings.employment_end_date}</span>
                )}
                {prorata.reason === 'started_mid_year' && (
                  <span className="reason-badge starting">📅 Started {settings.employment_start_date}</span>
                )}
                {prorata.reason === 'started_and_ending' && (
                  <span className="reason-badge both">📅 Started & Ending in year</span>
                )}
              </div>
              <div className="prorata-calculation">
                <div className="calc-row">
                  <span className="calc-label">Period:</span>
                  <span className="calc-value">
                    {prorata.effectiveStart.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} → {prorata.effectiveEnd.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                  </span>
                </div>
                <div className="calc-row">
                  <span className="calc-label">Days in period:</span>
                  <span className="calc-value">{prorata.workedDays} / {prorata.totalDaysInYear} ({prorata.percentage}%)</span>
                </div>
                <div className="calc-row result">
                  <span className="calc-label">Entitled to:</span>
                  <span className="calc-value highlight">
                    {prorata.total} × {prorata.percentage}% = <strong>{prorata.prorated} days</strong>
                  </span>
                </div>
              </div>
            </>
          ) : (
            <div className="prorata-info full-year">
              <span className="full-year-badge">✓ Full year entitlement</span>
              <span className="prorata-result">
                <strong>{prorata.total} days</strong>
              </span>
            </div>
          )}
        </div>
      )}

      {/* Delete */}
      <div className="detail-section danger-zone">
        <h4>🗑️ Danger Zone</h4>
        <button className="btn-danger" onClick={onDelete}>
          Delete This Job
        </button>
      </div>
    </div>
  );
}

// Add Job Modal
function AddJobModal({ onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    job_name: '',
    company_name: '',
    employment_start_date: '',
    employment_end_date: '',
    is_ongoing: true,
    year_type: 'calendar',
    base_days: 25,
    carried_over: 0,
    extra_days: 0,
    days_already_taken: 0,
    working_days: [1, 2, 3, 4, 5],
    exclude_bank_holidays: true,
    is_current: true,
    salary_amount: '',
    salary_currency: 'GBP',
    salary_period: 'yearly',
    hours_per_day: 8,
    schedule_type: 'fixed',
    schedule_notes: ''
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.job_name.trim()) {
      alert('Please enter a job title');
      return;
    }
    
    setSaving(true);
    try {
      const dataToSend = {
        ...formData,
        salary_amount: formData.salary_amount ? parseFloat(formData.salary_amount) : null,
        employment_end_date: formData.is_ongoing ? null : (formData.employment_end_date || null)
      };
      const response = await api.post('/employment/jobs', dataToSend);
      // Fetch the created job to get full data
      const jobsResponse = await api.get('/employment/jobs');
      const newJob = jobsResponse.data.find(j => j.id === response.data.id);
      onSuccess(newJob || { ...formData, id: response.data.id });
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to create job');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>💼 Add New Job</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <form onSubmit={handleSubmit}>
          {/* Job Info */}
          <div className="modal-section">
            <h3>Job Information</h3>
            <div className="form-row">
              <div className="form-group">
                <label>Job Title *</label>
                <input
                  type="text"
                  value={formData.job_name}
                  onChange={(e) => setFormData({ ...formData, job_name: e.target.value })}
                  placeholder="e.g. Software Engineer"
                  required
                  autoFocus
                />
              </div>
              <div className="form-group">
                <label>Company Name</label>
                <input
                  type="text"
                  value={formData.company_name}
                  onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                  placeholder="e.g. Acme Corp"
                />
              </div>
            </div>
          </div>

          {/* Employment Period */}
          <div className="modal-section">
            <h3>Employment Period</h3>
            <div className="form-row">
              <div className="form-group">
                <label>Start Date</label>
                <input
                  type="date"
                  value={formData.employment_start_date}
                  onChange={(e) => setFormData({ ...formData, employment_start_date: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>End Date</label>
                <input
                  type="date"
                  value={formData.employment_end_date}
                  onChange={(e) => setFormData({ ...formData, employment_end_date: e.target.value })}
                  disabled={formData.is_ongoing}
                />
              </div>
            </div>
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={formData.is_ongoing}
                onChange={(e) => setFormData({ 
                  ...formData, 
                  is_ongoing: e.target.checked,
                  employment_end_date: e.target.checked ? '' : formData.employment_end_date
                })}
              />
              <span>This is an ongoing position</span>
            </label>
          </div>

          {/* Salary */}
          <div className="modal-section">
            <h3>Salary (Optional)</h3>
            <div className="form-row three-cols">
              <div className="form-group">
                <label>Amount</label>
                <input
                  type="number"
                  value={formData.salary_amount}
                  onChange={(e) => setFormData({ ...formData, salary_amount: e.target.value })}
                  placeholder="e.g. 50000"
                  min="0"
                />
              </div>
              <div className="form-group">
                <label>Currency</label>
                <select
                  value={formData.salary_currency}
                  onChange={(e) => setFormData({ ...formData, salary_currency: e.target.value })}
                >
                  {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Period</label>
                <select
                  value={formData.salary_period}
                  onChange={(e) => setFormData({ ...formData, salary_period: e.target.value })}
                >
                  {SALARY_PERIODS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                </select>
              </div>
            </div>
          </div>

          {/* Leave & Schedule */}
          <div className="modal-section">
            <h3>Leave & Schedule</h3>
            <div className="form-row">
              <div className="form-group">
                <label>Annual Leave Days</label>
                <input
                  type="number"
                  value={formData.base_days}
                  onChange={(e) => setFormData({ ...formData, base_days: parseInt(e.target.value) || 0 })}
                  min="0"
                />
              </div>
              <div className="form-group">
                <label>Hours per Day</label>
                <input
                  type="number"
                  value={formData.hours_per_day}
                  onChange={(e) => setFormData({ ...formData, hours_per_day: parseFloat(e.target.value) || 8 })}
                  min="0"
                  max="24"
                  step="0.5"
                />
              </div>
            </div>
          </div>

          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={formData.is_current}
              onChange={(e) => setFormData({ ...formData, is_current: e.target.checked })}
            />
            <span>This is my current job</span>
          </label>

          <div className="modal-actions">
            <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving ? 'Creating...' : 'Create Job'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default Employment;
